import { useState, useEffect } from 'react';
import { CUSTOM_CSS_ID } from '@bg-shared/constants';
import { assetsService } from '@bg-shared/business';

const DARK_THEME_CLASS = 'dark-theme';
const DARK_THEME_EXTENDED_CLASS = 'dark-theme-extended';

const extractCSSVariables = (cssText: string) => {
    const variableRegex = /--[\w-]+:\s*[^;]+;/g;
    const matches = cssText.match(variableRegex);
    return matches ? matches.join('\n  ') : '';
};

// Function is doing 2 things:
// 1. Copies partners custom css variables to dark theme
// 2. If partner modified place bet button, doesn't apply all our dark theme styles
const applyCustomCssToDarkTheme = () => {
    const customStyle = document.getElementById(CUSTOM_CSS_ID);

    if (!customStyle) {
        document.documentElement.classList.add(DARK_THEME_EXTENDED_CLASS);
        return;
    }

    const currentCSS = customStyle.textContent || '';

    const hasCustomBetStyles =
        currentCSS.includes('--place-bet-button') || currentCSS.includes('.place-bet-button');

    if (!hasCustomBetStyles) {
        document.documentElement.classList.add(DARK_THEME_EXTENDED_CLASS);
    }

    const rootRegex = /:root\s*{([^}]*)}/;
    const rootMatch = currentCSS.match(rootRegex);

    if (rootMatch && rootMatch[1]) {
        const darkThemeCSS = `:root.${DARK_THEME_CLASS} {\n  ${extractCSSVariables(
            rootMatch[1],
        )}\n}`;

        customStyle.textContent = `${currentCSS}\n\n${darkThemeCSS}`;
    } else {
        document.documentElement.classList.add(DARK_THEME_EXTENDED_CLASS);
    }
};

interface IOptions {
    forceDarkTheme?: boolean;
}

export const useToggleTheme = ({ forceDarkTheme = false }: IOptions) => {
    const [isDarkTheme, setIsDarkTheme] = useState(false);

    useEffect(() => {
        if (!forceDarkTheme) {
            return undefined;
        }

        document.documentElement.classList.add(DARK_THEME_CLASS);
        setIsDarkTheme(true);
        applyCustomCssToDarkTheme();

        setTimeout(assetsService.generateCssVarModifications, 300);

        return () => {
            document.documentElement.classList.remove(DARK_THEME_CLASS, DARK_THEME_EXTENDED_CLASS);
            assetsService.generateCssVarModifications();
        };
    }, [forceDarkTheme]);

    return { isDarkTheme };
};
